
















import { bus } from "@/bus";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { fixedHtmlData, FixedHtmlProperties, fileUploadStages, fixedHtmlCSS } from "@/services/posts/postTypes/fixedhtml";
import { getContentTemplateWithoutAclCheck } from "@/services/contentTemplates/contentTemplateService";
import { getVueTemplateAndData } from "@/services/posts/postsService";
import { RuntimeTemplateCompiler } from "vue-runtime-template-compiler";
import FileSelectModal from "@/components/files/modals/FileSelectModal.vue";
import DirectFileUpload from "@/components/common/files/DirectFileUpload.vue";
import { MediaType } from "@/services/files/types";
import { FILEENDPOINT } from "@/app.config";
import { FolderEntites } from "@/services/folders/types";

// Components
import ImageComponentForm from "./formComponents/ImageComponentForm.vue";
import TextComponentForm from "./formComponents/TextComponentForm.vue";
import FormatTextComponentForm from "./formComponents/FormatTextComponentForm.vue";
import MacrosComponentForm from "./formComponents/MacrosComponentForm.vue";
import { ChannelStyle, Style } from "@/services/styles/types";
import { getChannelStyle } from "@/services/channels/channelsService";
import QrcodeComponentForm from "./formComponents/QrcodeComponentForm.vue";
import SelectStockImageModal from "@/components/files/modals/SelectStockImageModal.vue";

@Component({
    components: {
        imagecomponentform: ImageComponentForm,
        textcomponentform: TextComponentForm,
        formattextcomponentform: FormatTextComponentForm,
        macroscomponentform: MacrosComponentForm,
        qrcodecomponentform: QrcodeComponentForm,
        RuntimeTemplateCompiler,
        FileSelectModal,
        DirectFileUpload,
        SelectStockImageModal
    }
})
export default class FixedHtmlForm extends Vue {
    @Getter("getAllowStockImages") allowStockImages!: boolean;
    // Props from _FixedHtmlTemplateForm used for the preview when you are creating a Template
    @Prop() itemStyle!: Style; // Styles defined on PostStyle
    @Prop() templateSourceCode!: string; // Template Code

    // Props from _PostForm, used for styles when you are Creating a Post,
    @Prop() private channelId!: string; // Used to get the fixedhtml styles attached to respective channel.
    @Prop() private contentTemplateId!: string;

    // Props used in both scenarios ( creating a template or creating a post)
    @Prop() private properties!: FixedHtmlProperties;
    @Prop() channelHeight!: number;
    @Prop() channelWidth!: number;

    localProperties = {} as FixedHtmlProperties;
    update = false;
    type = MediaType.Image;
    currentImageId = "";
    folders = FolderEntites;
    fileuploadinprogress = { fileIsUploading: false, imageId: "", failedUploading: false } as fileUploadStages;
    templateToParse: string = "";
    channelStyleInfo: ChannelStyle | {} = {};
    stylesForPost = "";

    // Watcher
    @Watch("templateSourceCode") templateReAssignment() {
        this.templateToParse = this.templateSourceCode;
    }

    // when we are creating / editing a post we bring the styles applied for this channel and store them in a locally
    created() {
        if (this.channelId) {
            getChannelStyle(this.channelId, "fixedhtml").then(response => {
                this.channelStyleInfo = response;
                this.stylesForPost = (this.channelStyleInfo as ChannelStyle).css.wrapper;
            });
        }
    }

    mounted() {
        // when we are creating / editing a template we want the changes to be local to the browser
        // when we are creating / editing a post we are fetching the template via its Id from backend

        if (this.properties) {
            this.localProperties = this.properties;
        } else {
            this.$emit("setDefaultValues", JSON.stringify(this.localProperties));
        }

        if (!this.localProperties.contentTemplateId) {
            this.localProperties.contentTemplateId = this.contentTemplateId;
        }

        if (this.localProperties.contentTemplateId) {
            getContentTemplateWithoutAclCheck(this.localProperties.contentTemplateId).then(response => {
                this.templateToParse = response.content.html;
            });
        } else {
            this.templateToParse = this.templateSourceCode;
        }

        bus.$on("openFileSelect", (id: string) => {
            this.currentImageId = id;
            (this.$refs.fileSelectModal as any).open();
        });
        bus.$on("openDirectUpload", (id: string) => {
            this.currentImageId = id;
            (this.$refs.directUpload as any).openFileUpload();
        });
        bus.$on("stockImageDownload", (id: string) => {
            this.currentImageId = id;
            (this.$refs.selectStockImageModal as any).open();
        });
        bus.$on("injectFile", (file: any, id: string) => {
            this.currentImageId = id;
            (this.$refs.directUpload as any).injectFile(file);
        });
    }

    destroyed() {
        bus.$off("openFileSelect");
        bus.$off("openDirectUpload");
        bus.$off("injectFile");
    }

    get channelSize() {
        return `width: ${this.channelWidth}px;
                height: ${this.channelHeight}px;`;
    }

    clearDataOnPreview() {
        this.localProperties.data = [];
    }

    onSelect(file: any) {
        bus.$emit("changeImage", this.currentImageId, FILEENDPOINT + file.fileId);
        this.$bvModal.hide("modal-fileSelect");
        this.fileuploadinprogress.fileIsUploading = false;
        this.fileuploadinprogress.imageId = "";
    }

    onStockFileUploaded(file: any) {
        bus.$emit("changeImage", this.currentImageId, FILEENDPOINT + file.fileId);
        this.$bvModal.hide("modal-stockImages");
    }

    emitData() {
        bus.$emit("getComponentData"); // Get data from each individual component and update the main template object
        this.$emit("propertiesUpdated", this.localProperties);
    }

    get styleModel(): string {
        let style = "";
        if (this.itemStyle && this.itemStyle.css as fixedHtmlCSS) {
            const css = this.itemStyle.css as fixedHtmlCSS;
            style = css.wrapper || "";
        } else {
            style = this.stylesForPost;
        }

        if (this.channelWidth || this.channelHeight) {
            style += ` width: ${this.channelWidth}px; height: ${this.channelHeight}px; --channel-width: ${this.channelWidth}px; --channel-height: ${this.channelHeight}px;`;
        }

        return style;
    }

    get templateToRender():string {
        bus.$emit("getComponentData");
        var templateAndData = getVueTemplateAndData(this.templateToParse);
        const { template, data } = templateAndData;
        const keysThatContainData = ["id", "text", "src", "behavior", "qrCodeValue", "macros"];
        // Match the data without altering fields that might contain user-data
        if (!this.localProperties.data) {
            this.localProperties.data = data;
        } else {
            for (const i in data) {
                const element: fixedHtmlData = data[i];
                const localElement: fixedHtmlData = this.localProperties.data.filter(x => x.id === element.id)[0];

                if (!localElement) this.localProperties.data.push(element);
                else {
                    Object.keys(element).forEach(key => {
                        if (!keysThatContainData.includes(key)) {
                            localElement[key] = element[key];
                        }
                    });
                }
            }
        }

        this.update = !this.update;
        return template;
    }

    fileUploadFailed() {
        this.fileuploadinprogress.fileIsUploading = false;
        this.fileuploadinprogress.failedUploading = true;
        setTimeout(() => {
            this.fileuploadinprogress.failedUploading = false;
            this.fileuploadinprogress.imageId = "";
        }, 2000);
    }

    uploadStarted() {
        this.fileuploadinprogress.imageId = this.currentImageId;
        this.fileuploadinprogress.fileIsUploading = true;
    }
}
